/* eslint-disable */
import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { LifeBuoy, X, Send, Paperclip, Image as ImageIcon } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Support Center Chat — human-like assistants (Anna, Gaëlle, Jeff).
 * Multi-language (respects the user's dashboard language).
 * Persistent conversations. Users can attach screenshots/photos as proof.
 */
const SupportCenter = ({ userId, userName, openTrigger, onClose }) => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [action, setAction] = useState("");
  const [sending, setSending] = useState(false);
  const [agentName, setAgentName] = useState("Anna");
  const [conversationId, setConversationId] = useState(null);
  const [attachedImage, setAttachedImage] = useState(null); // { url, dataUrl, name }
  const [uploadingImg, setUploadingImg] = useState(false);
  const fileInputRef = useRef(null);
  const scrollRef = useRef(null);
  const initials = (agentName || "A").charAt(0).toUpperCase();

  const currentLang = (typeof window !== "undefined" && localStorage.getItem("vy_lang")) || "fr";

  const T = {
    ht: { title: "Sipò Vape Yard", online: "En liy • Rezolisyon nan mwens ke 24 èdtan", typing: "ap ekri...", placeholderQ: "Poze kesyon...", placeholderR: "Detay pwoblèm...", err: "Gen yon pwoblèm koneksyon. Reeseye.", greet: "Bonjou! Chwazi sa w bezwen:", ask_hint: "Ekri m kesyon w — mwen ka reponn ou sou komisyon, stock, credit score, oswa règ platfòm nan. Ou ka tache yon foto tou si sa ap ede 📎", report_hint: "Ekri detay pwoblèm w — ou ka tache yon foto screenshot kòm prèv 📎. Ekip teknik la ap trete sa.", intv_hint: "Dekri entèvansyon teknik w bezwen an — ou ka ajoute foto 📎 pou nou wè sa k ap pase. Nou la pou w.", opt_q: "❓ Kesyon", opt_r: "📢 Rapòte", opt_i: "🛠 Teknik", opt_end: "✅ Mèsi", img_too_big: "Imaj la twò gwo (max 10MB)", img_sent: "📎 Foto tache", img_uploading: "Ap voye foto...", attach_btn: "Tache yon foto" },
    fr: { title: "Support Vape Yard", online: "En ligne • Résolution en moins de 24h", typing: "écrit...", placeholderQ: "Posez votre question...", placeholderR: "Détails du problème...", err: "Problème de connexion. Réessayez.", greet: "Bonjour ! Choisissez ce dont vous avez besoin :", ask_hint: "Écrivez votre question — commissions, stock, credit score, règles. Vous pouvez joindre une photo 📎", report_hint: "Décrivez le problème — joignez une photo screenshot comme preuve 📎.", intv_hint: "Décrivez l'intervention technique — ajoutez des photos 📎 si utile.", opt_q: "❓ Question", opt_r: "📢 Signaler", opt_i: "🛠 Technique", opt_end: "✅ Merci", img_too_big: "Image trop volumineuse (max 10 Mo)", img_sent: "📎 Photo jointe", img_uploading: "Envoi photo...", attach_btn: "Joindre une photo" },
    en: { title: "Vape Yard Support", online: "Online • Resolution in under 24h", typing: "is typing...", placeholderQ: "Ask a question...", placeholderR: "Problem details...", err: "Connection issue. Try again.", greet: "Hi! What do you need help with?", ask_hint: "Ask your question — commissions, stock, credit score, rules. You can attach a photo 📎", report_hint: "Describe the issue — attach a screenshot as proof 📎.", intv_hint: "Describe the technical intervention — add photos 📎 if helpful.", opt_q: "❓ Ask", opt_r: "📢 Report", opt_i: "🛠 Tech", opt_end: "✅ Thanks", img_too_big: "Image too large (max 10MB)", img_sent: "📎 Photo attached", img_uploading: "Uploading photo...", attach_btn: "Attach a photo" },
    es: { title: "Soporte Vape Yard", online: "En línea • Resolución en menos de 24h", typing: "está escribiendo...", placeholderQ: "Haz tu pregunta...", placeholderR: "Detalles del problema...", err: "Problema de conexión. Reintenta.", greet: "¡Hola! ¿Con qué necesitas ayuda?", ask_hint: "Haz tu pregunta — puedes adjuntar una foto 📎", report_hint: "Describe el problema — adjunta una captura como prueba 📎.", intv_hint: "Describe la intervención técnica — añade fotos 📎 si ayuda.", opt_q: "❓ Pregunta", opt_r: "📢 Reportar", opt_i: "🛠 Técnico", opt_end: "✅ Gracias", img_too_big: "Imagen muy grande (máx 10MB)", img_sent: "📎 Foto adjunta", img_uploading: "Subiendo foto...", attach_btn: "Adjuntar foto" },
  };
  const t = T[currentLang] || T.ht;

  const postChat = async (payload) => {
    const body = { user_id: userId, user_name: userName, lang: currentLang, ...payload };
    if (conversationId) body.conversation_id = conversationId;
    const r = await axios.post(`${API}/support/chat`, body);
    if (r.data.agent_name) setAgentName(r.data.agent_name);
    if (r.data.conversation_id) setConversationId(r.data.conversation_id);
    return r.data;
  };

  const greet = async () => {
    try {
      const d = await postChat({ action: "greet" });
      setMessages([{ role: "agent", content: d.response, options: d.options }]);
    } catch (_) {
      setMessages([{ role: "agent", content: t.greet, options: [
        { id: "question", label: t.opt_q }, { id: "report", label: t.opt_r },
        { id: "intervention", label: t.opt_i }, { id: "end", label: t.opt_end },
      ]}]);
    }
  };

  useEffect(() => {
    if (open && messages.length === 0) greet();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => { if (openTrigger) setOpen(true); }, [openTrigger]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const chooseOption = async (opt) => {
    setMessages(prev => [...prev, { role: "user", content: opt.label }]);
    if (opt.id === "end") {
      try {
        const d = await postChat({ action: "end" });
        setMessages(prev => [...prev, { role: "agent", content: d.response }]);
      } catch (_) {}
      setAction("");
      return;
    }
    setAction(opt.id);
    const hintMap = { question: t.ask_hint, report: t.report_hint, intervention: t.intv_hint };
    setMessages(prev => [...prev, { role: "agent", content: hintMap[opt.id] || "..." }]);
  };

  const onFilePicked = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = ""; // allow re-pick same file
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) {
      setMessages(prev => [...prev, { role: "agent", content: t.img_too_big }]);
      return;
    }
    const dataUrl = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(f);
    });
    setUploadingImg(true);
    try {
      const base64 = dataUrl.split(",", 2)[1];
      const resp = await axios.post(`${API}/support/upload-image`, {
        image_base64: base64,
        mime_type: f.type || "image/png",
        conversation_id: conversationId || undefined,
        user_id: userId,
      });
      const absUrl = `${BACKEND_URL}${resp.data.image_url}`;
      setAttachedImage({ url: resp.data.image_url, absUrl, dataUrl, name: f.name, base64, mime: f.type || "image/png" });
    } catch (err) {
      setMessages(prev => [...prev, { role: "agent", content: t.err }]);
    } finally {
      setUploadingImg(false);
    }
  };

  const send = async () => {
    if ((!input.trim() && !attachedImage) || !action) return;
    const msg = input.trim();
    const img = attachedImage;
    setMessages(prev => [...prev, { role: "user", content: msg, image: img?.dataUrl }]);
    setInput("");
    setAttachedImage(null);
    setSending(true);
    try {
      const payload = { action, message: msg || (img ? (t.img_sent) : "") };
      if (img) {
        payload.image_url = img.url;
        payload.image_base64 = img.base64; // fallback for vision in case static path is slow
      }
      const d = await postChat(payload);
      // Human-like typing delay proportional to response length (~30 chars/sec, capped 800ms–4s)
      const responseLen = (d.response || "").length;
      const delay = Math.min(4000, Math.max(800, responseLen * 25));
      await new Promise(r => setTimeout(r, delay));
      setMessages(prev => [...prev, { role: "agent", content: d.response, options: d.options, acknowledged: d.acknowledged, action_executed: d.action_executed }]);
      if (d.acknowledged) setAction("");
    } catch (e) {
      setMessages(prev => [...prev, { role: "agent", content: t.err }]);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 z-40 bg-emerald-600 hover:bg-emerald-500 text-white rounded-full shadow-2xl w-14 h-14 flex items-center justify-center transition-all active:scale-95"
        data-testid="support-center-btn"
        title={t.title}
      >
        <LifeBuoy className="w-6 h-6" />
      </button>

      {open && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => { setOpen(false); onClose?.(); }}>
          <div className="bg-zinc-900 border border-emerald-500/40 rounded-2xl w-full max-w-md h-[600px] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()} data-testid="support-center-modal">
            <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800 bg-gradient-to-r from-emerald-900/40 to-teal-900/40">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-full bg-emerald-500 flex items-center justify-center text-white font-black" data-testid="support-agent-initial">{initials}</div>
                <div>
                  <p className="text-white font-bold text-sm" data-testid="support-agent-name">{agentName} — {t.title}</p>
                  <p className="text-emerald-400 text-[10px]">{t.online}</p>
                </div>
              </div>
              <button onClick={() => { setOpen(false); onClose?.(); }} className="text-zinc-500 hover:text-white"><X className="w-5 h-5" /></button>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-3 space-y-2">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${m.role === "user" ? "bg-violet-600 text-white" : "bg-zinc-800 text-zinc-100"}`}>
                    {m.image && (
                      <img src={m.image} alt="attached" className="rounded-lg mb-2 max-h-48 object-contain border border-white/10" data-testid={`support-msg-img-${i}`} />
                    )}
                    {m.content && <p className="whitespace-pre-wrap">{m.content}</p>}
                    {m.action_executed?.name === "recalc_user" && (
                      <div className="mt-2 text-[11px] bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 rounded-lg px-2 py-1 font-bold" data-testid={`support-action-recalc-${i}`}>
                        🔄 Chif ou yo rekalkile otomatikman
                      </div>
                    )}
                    {m.action_executed?.name === "escalate_tech" && (
                      <div className="mt-2 text-[11px] bg-amber-500/15 border border-amber-500/40 text-amber-300 rounded-lg px-2 py-1 font-bold" data-testid={`support-action-escalate-${i}`}>
                        ⏳ Ka ou transfere bay ekip teknik la
                      </div>
                    )}
                    {m.options && m.options.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {m.options.map(o => (
                          <button key={o.id} onClick={() => chooseOption(o)} className="bg-emerald-600/80 hover:bg-emerald-500 text-white text-xs font-bold px-3 py-1.5 rounded-full" data-testid={`support-option-${o.id}`}>
                            {o.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {sending && (
                <div className="flex justify-start"><div className="bg-zinc-800 rounded-2xl px-3 py-2 flex items-center gap-2">
                  <span className="flex gap-1">
                    <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-bounce" style={{animationDelay:'0ms'}}></span>
                    <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-bounce" style={{animationDelay:'150ms'}}></span>
                    <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-bounce" style={{animationDelay:'300ms'}}></span>
                  </span>
                  <span className="text-zinc-400 text-xs italic">{agentName} {t.typing}</span>
                </div></div>
              )}
            </div>

            {action && (
              <div className="border-t border-zinc-800">
                {/* Image preview strip */}
                {attachedImage && (
                  <div className="flex items-center gap-2 px-3 py-2 bg-zinc-950/50">
                    <img src={attachedImage.dataUrl} alt="preview" className="w-12 h-12 object-cover rounded-md border border-emerald-500/40" data-testid="support-attached-preview" />
                    <span className="text-emerald-300 text-xs truncate flex-1">{attachedImage.name}</span>
                    <button onClick={() => setAttachedImage(null)} className="text-zinc-500 hover:text-red-400" data-testid="support-attached-clear"><X className="w-4 h-4" /></button>
                  </div>
                )}
                {uploadingImg && (
                  <div className="px-3 py-1.5 text-[11px] text-emerald-300 bg-emerald-500/10">{t.img_uploading}</div>
                )}

                <div className="flex gap-2 px-3 py-2 items-center">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    onChange={onFilePicked}
                    className="hidden"
                    data-testid="support-file-input"
                  />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={sending || uploadingImg}
                    title={t.attach_btn}
                    className="bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-emerald-400 hover:text-emerald-300 rounded-full p-2 transition-colors disabled:opacity-40"
                    data-testid="support-attach-btn"
                  >
                    <Paperclip className="w-4 h-4" />
                  </button>
                  <input
                    type="text"
                    value={input}
                    onChange={e => setInput(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter") send(); }}
                    placeholder={action === "question" ? t.placeholderQ : t.placeholderR}
                    disabled={sending}
                    className="flex-1 bg-zinc-800 border border-zinc-700 rounded-full px-3 py-2 text-white text-sm outline-none focus:border-emerald-500"
                    data-testid="support-input"
                  />
                  <button
                    onClick={send}
                    disabled={sending || (!input.trim() && !attachedImage)}
                    className="bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 text-white rounded-full p-2"
                    data-testid="support-send-btn"
                  >
                    <Send className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default SupportCenter;
